import { tKey, type I18nKey } from "@/core/i18n";
import { useElapsed } from "@/hooks/useElapsed";
import { Check, type LucideIcon } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

export interface StartupStep {
  key: string;
  label: I18nKey<"model">;
  icon: LucideIcon;
  estimatedMs: number;
}

export interface StartupScreenProps {
  /** Icon shown inside the pulsing badge */
  icon: LucideIcon;
  heading: string;
  subtitle: string;
  steps: StartupStep[];
  tips?: I18nKey<"model">[];
  /**
   * Wall-clock start of the work being waited on — ISO string or epoch ms.
   * Elapsed time and step progression are measured from here, so a reload or
   * a late visit shows the real age of the job instead of restarting at 0s.
   * Falls back to mount time when omitted.
   */
  startedAt?: string | number | null;
  /** Appended to the elapsed-time footer, e.g. "safe to leave this page" */
  footerNote?: string;
  /** Rendered below the footer, e.g. a skip button */
  action?: ReactNode;
}

export const DEFAULT_TIPS: I18nKey<"model">[] = [
  tKey("model:startup.tips.coldStart"),
  tKey("model:startup.tips.firstEpoch"),
  tKey("model:startup.tips.augmentationsOnTheFly"),
  tKey("model:startup.tips.closeTab"),
  tKey("model:startup.tips.annotationsOverEpochs"),
  tKey("model:startup.tips.lossFluctuation"),
  tKey("model:startup.tips.balancedLabels"),
  tKey("model:startup.tips.overfitting"),
  tKey("model:startup.tips.lowerLearningRate"),
  tKey("model:startup.tips.datasetSplit"),
  tKey("model:startup.tips.examplesPerLabel"),
  tKey("model:startup.tips.cleanDataset"),
  tKey("model:startup.tips.consistentLighting"),
  tKey("model:startup.tips.negativeImages"),
  tKey("model:startup.tips.tightBoxes"),
  tKey("model:startup.tips.rvc4"),
  tKey("model:startup.tips.onnx"),
  tKey("model:startup.tips.duplicateModel"),
  tKey("model:startup.tips.batchSize"),
  tKey("model:startup.tips.earlyStopping"),
  tKey("model:startup.tips.shortcuts"),
  tKey("model:startup.tips.classImbalance"),
  tKey("model:startup.tips.unseenDefects"),
  tKey("model:startup.tips.tooFast"),
  tKey("model:startup.tips.confusionMatrix"),
  tKey("model:startup.tips.segmentationMasks"),
  tKey("model:startup.tips.grayscale"),
  tKey("model:startup.tips.learningRate"),
  tKey("model:startup.tips.freezeBackbone"),
  tKey("model:startup.tips.resolution"),
];

const useRotatingTip = <T,>(tips: T[]) => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % tips.length), 6_000);
    return () => clearInterval(id);
  }, [tips.length]);
  return tips[index];
};

/**
 * Full-page waiting screen with a pulsing badge, time-based fake step
 * progression, rotating tips, and an elapsed-time footer. Progress is purely
 * cosmetic — the last step's bar clamps until the real work finishes. Pass
 * `startedAt` whenever the work outlives the mount (a job already running when
 * the page opens), otherwise the clock restarts on every visit.
 */
export const StartupScreen = ({
  icon: BadgeIcon,
  heading,
  subtitle,
  steps,
  tips = DEFAULT_TIPS,
  startedAt,
  footerNote,
  action,
}: StartupScreenProps) => {
  const { t } = useTranslation(["model", "common"]);
  const elapsed = useElapsed(startedAt);
  const tip = useRotatingTip(tips);

  let remaining = elapsed;
  let activeIndex = steps.length - 1;
  for (let i = 0; i < steps.length; i++) {
    if (remaining < steps[i].estimatedMs) {
      activeIndex = i;
      break;
    }
    remaining -= steps[i].estimatedMs;
  }

  const activeStep = steps[activeIndex];
  const stepProgress = Math.min(
    100,
    (remaining / activeStep.estimatedMs) * 100,
  );

  const seconds = Math.floor(elapsed / 1000);
  const elapsedLabel =
    seconds < 60
      ? `${seconds}s`
      : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;

  return (
    // The whole screen must fit without any scrollbar: content is compact,
    // centered via m-auto, and the tips block drops out on short viewports.
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="m-auto flex w-full max-w-xl flex-col items-center gap-5 px-10 py-6">
        <div className="relative flex flex-col items-center gap-2 text-center">
          {/* Ping halos sit at inset-5/inset-6 so their 2x scale peaks exactly
            at the box edge — they can never overflow and get clipped. */}
          <div className="relative flex h-20 w-20 items-center justify-center">
            <span className="absolute inset-5 animate-ping rounded-full bg-emerald-400/40" />
            <span
              className="absolute inset-6 animate-ping rounded-full bg-emerald-500/30"
              style={{ animationDelay: "0.4s" }}
            />
            <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg shadow-emerald-500/30">
              <BadgeIcon className="size-6" />
            </span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">{heading}</h3>
          <p className="max-w-lg text-sm text-gray-500">{subtitle}</p>
        </div>

        <div className="relative z-10 flex w-full flex-col gap-2">
          {steps.map((step, i) => {
            const Icon = step.icon;
            const isDone = i < activeIndex;
            const isActive = i === activeIndex;
            return (
              <div
                key={step.key}
                className={`flex items-center gap-4 rounded-xl border px-4 py-2 transition-colors ${
                  isActive
                    ? "border-emerald-300 bg-white shadow-sm"
                    : isDone
                      ? "border-emerald-100 bg-emerald-50/60"
                      : "border-gray-200 bg-white/50"
                }`}
              >
                <div
                  className={`flex size-8 items-center justify-center rounded-full ${
                    isDone
                      ? "bg-emerald-500 text-white"
                      : isActive
                        ? "bg-emerald-100 text-emerald-600"
                        : "bg-gray-100 text-gray-400"
                  }`}
                >
                  {isDone ? (
                    <Check className="size-4" />
                  ) : (
                    <Icon
                      className={`size-4 ${isActive ? "animate-pulse" : ""}`}
                    />
                  )}
                </div>
                <div className="flex flex-1 flex-col gap-1">
                  <span
                    className={`text-sm font-medium ${
                      isActive
                        ? "text-gray-900"
                        : isDone
                          ? "text-emerald-700"
                          : "text-gray-500"
                    }`}
                  >
                    {t(step.label)}
                  </span>
                  {isActive && (
                    <div className="h-1 w-full overflow-hidden rounded-full bg-emerald-100">
                      <div
                        className="h-full rounded-full bg-emerald-500 transition-[width] duration-500 ease-linear"
                        style={{ width: `${stepProgress}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Nice-to-have filler — dropped on short viewports so the essential
            content (steps, elapsed, action) always fits without scrolling. */}
        <div className="relative z-10 hidden w-full flex-col items-center gap-1 text-center [@media(min-height:750px)]:flex">
          <span className="text-xs font-semibold uppercase tracking-wider text-emerald-600">
            {t("startup.didYouKnow")}
          </span>
          <p
            key={tip}
            className="animate-in fade-in text-sm text-gray-600 duration-700"
          >
            {t(tip)}
          </p>
        </div>

        <div className="relative z-10 text-xs text-gray-400">
          {t("startup.elapsed", { time: elapsedLabel })}
          {footerNote ? ` · ${footerNote}` : ""}
        </div>

        {action && <div className="relative z-10">{action}</div>}
      </div>
    </div>
  );
};
